import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import MaskInput from 'components/MaskedInput';
import useCurrency from 'hooks/useCurreny';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

interface PricesSectionProps {
  pricesN: string;
  setPricesN: (value: string) => void;
  pricesP: string;
  setPricesP: (value: string) => void;
  pricesK: string;
  setPricesK: (value: string) => void;
  efficiencyN: string;
  setEfficiencyN: (value: string) => void;
  efficiencyP: string;
  setEfficiencyP: (value: string) => void;
  efficiencyK: string;
  setEfficiencyK: (value: string) => void;
  kg: string;
  onlyAc: string;
}

const PricesSection: FC<PricesSectionProps> = ({
  pricesN,
  setPricesN,
  pricesP,
  setPricesP,
  pricesK,
  setPricesK,
  efficiencyN,
  setEfficiencyN,
  efficiencyP,
  setEfficiencyP,
  efficiencyK,
  setEfficiencyK,
  kg,
  onlyAc,
}) => {
  const { t } = useTranslation();
  const { currencyGreen } = useCurrency();
  const { isExpertMode, isMaintenanceMode } = useCroptimizer();
  
  const renderInput = useMemo(() => {
    const commonProps = {
      disabled: true,
      className: 'w-full',
      titleClassName: 'top-[-9px] left-[8px] bg-disable-gradient',
      isBlueBg: true,
    };

    return (
      <div className="flex mx-[-0.5rem]">
        <div className="w-1/3 px-2 break-inside-avoid">
          <MaskInput
            title="N"
            value={efficiencyN}
            onChange={setEfficiencyN}
            {...commonProps}
          />
        </div>
        <div className="w-1/3 px-2 break-inside-avoid">
          <MaskInput
            title={
              <div>
                P<sub>2</sub>O<sub>5</sub>
              </div>
            }
            value={efficiencyP}
            onChange={setEfficiencyP}
            {...commonProps}
          />
        </div>
        <div className="w-1/3 px-2 break-inside-avoid">
          <MaskInput
            title={
              <div>
                K<sub>2</sub>O
              </div>
            }
            value={efficiencyK}
            onChange={setEfficiencyK}
            {...commonProps}
          />
        </div>
      </div>
    );
  }, [
    efficiencyN,
    efficiencyP,
    efficiencyK,
    setEfficiencyN,
    setEfficiencyP,
    setEfficiencyK,
  ]);

  return (
    <div className="border-b border-primitiveGrey py-8 break-inside-avoid">
      <div className="text-black font-bold md:text-lg mb-8">
        {t('npkPrices')} ({currencyGreen}/{kg})
      </div>
      <div className="flex mx-[-0.5rem] mb-4 break-inside-avoid">
        <div className="w-1/3 px-2 break-inside-avoid">
          <MaskInput
            titleClassName="top-[-9px] left-[8px]"
            title="N"
            className="w-full"
            value={pricesN}
            onChange={setPricesN}
            isBlueBg
          />
        </div>
        <div className="w-1/3 px-2 break-inside-avoid">
          <MaskInput
            titleClassName="top-[-9px] left-[8px]"
            title={
              <div>
                P<sub>2</sub>O<sub>5</sub>
              </div>
            }
            className="w-full"
            value={pricesP}
            onChange={setPricesP}
            isBlueBg
          />
        </div>
        <div className="w-1/3 px-2 break-inside-avoid">
          <MaskInput
            titleClassName="top-[-9px] left-[8px]"
            title={
              <div>
                K<sub>2</sub>O
              </div>
            }
            className="w-full"
            value={pricesK}
            onChange={setPricesK}
            isBlueBg
          />
        </div>
      </div>
      {isExpertMode && 
        <>
          <div className="text-black font-bold md:text-lg mb-8 mt-4">
            {t('npkEffiGain')} ({currencyGreen}/{onlyAc})
          </div>
          {isMaintenanceMode ? (
            <div className="px-2 py-3 bg-gray-50 rounded-lg">
              <p className="text-gray-600 text-sm">
                Pas de gain d&apos;efficacité NPK calculé si pH actuel = pH visé. Attention aux impasses, cliquer sur l&apos;onglet Strategy « Vue 5 ans » pour consulter les &apos;Pertes potentielles sur 5 ans sans entretenir le pH&apos;
              </p>
            </div>
          ) : (
            renderInput
          )}
        </>
      }
    </div>
  );
};

export default PricesSection;
